import React, { useState } from 'react';
import { MonitoringConfig } from '../types';
import { Calendar, MapPin, Clock, Users, Settings } from 'lucide-react';

interface MonitoringFormProps {
  onStartMonitoring: (config: MonitoringConfig) => void;
  isLoading: boolean;
  isMonitoring: boolean;
}

export const MonitoringForm: React.FC<MonitoringFormProps> = ({
  onStartMonitoring,
  isLoading,
  isMonitoring,
}) => {
  const [config, setConfig] = useState<MonitoringConfig>({
    start_place: 'BENGALURU',
    end_place: 'COIMBATORE',
    journey_date: '',
    required_seats: [],
    from_hour: 18,
    to_hour: 24,
    sleep_duration: 300,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [seatInput, setSeatInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formattedConfig = {
      ...config,
      required_seats: seatInput.split(',').map(s => s.trim()).filter(s => s),
    };
    onStartMonitoring(formattedConfig);
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  React.useEffect(() => {
    if (!config.journey_date) {
      setConfig(prev => ({ ...prev, journey_date: getTomorrowDate() }));
    }
  }, [config.journey_date]);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Bus Route Monitor
        </h2>
      </div>
      
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Route Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              From
            </label>
            <select
              value={config.start_place}
              onChange={(e) => setConfig(prev => ({ ...prev, start_place: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="BENGALURU">Bengaluru</option>
              <option value="COIMBATORE">Coimbatore</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              To
            </label>
            <select
              value={config.end_place}
              onChange={(e) => setConfig(prev => ({ ...prev, end_place: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="COIMBATORE">Coimbatore</option>
              <option value="BENGALURU">Bengaluru</option>
            </select>
          </div>
        </div>

        {/* Journey Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Journey Date
          </label>
          <input
            type="text"
            value={config.journey_date}
            onChange={(e) => setConfig(prev => ({ ...prev, journey_date: e.target.value }))}
            placeholder="DD/MM/YYYY"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Format: DD/MM/YYYY (e.g., {getTomorrowDate()})</p>
        </div>

        {/* Advanced Options Toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <Settings className="w-4 h-4" />
            Advanced Options
            <span className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}>▼</span>
          </button>
        </div>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            {/* Specific Seats */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Specific Seats (Optional)
              </label>
              <input
                type="text"
                value={seatInput}
                onChange={(e) => setSeatInput(e.target.value)}
                placeholder="1A, 2A, 3B"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated seat numbers</p>
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  From Hour
                </label>
                <select
                  value={config.from_hour || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, from_hour: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Any time</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  To Hour
                </label>
                <select
                  value={config.to_hour || ''}
                  onChange={(e) => setConfig(prev => ({ ...prev, to_hour: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Any time</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sleep Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check Interval (seconds)
              </label>
              <input
                type="number"
                value={config.sleep_duration}
                onChange={(e) => setConfig(prev => ({ ...prev, sleep_duration: parseInt(e.target.value) }))}
                min="60"
                max="3600"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">How often to check for updates (60-3600 seconds)</p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || isMonitoring}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
            isLoading || isMonitoring
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg'
          } text-white`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              Starting Monitor...
            </span>
          ) : isMonitoring ? (
            'Monitoring Active'
          ) : (
            'Start Monitoring'
          )}
        </button>
      </form>
    </div>
  );
}; 